import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ReactNode } from "react";
import {
  Tooltip as UiTooltip,
  TooltipContent as UiTooltipContent,
  TooltipProvider as UiTooltipProvider,
  TooltipTrigger as UiTooltipTrigger,
} from "@/components/ui/tooltip";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type StatsCardProps = {
  title: string;
  value: ReactNode;
  icon: ReactNode;
  iconTooltip?: ReactNode;
  description?: string;
  trend?: {
    value: number;
    isPositive: boolean;
    period?: string;
    absoluteChange?: number;
  };
  footer?: ReactNode;
};

const StatsCard = ({
  title,
  value,
  icon,
  iconTooltip,
  description,
  trend,
  footer,
}: StatsCardProps) => {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {iconTooltip ? (
          <UiTooltipProvider>
            <UiTooltip>
              <UiTooltipTrigger asChild>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-primary/10 text-primary cursor-help">
                  {icon}
                </div>
              </UiTooltipTrigger>
              <UiTooltipContent side="top" className="text-xs">
                {iconTooltip}
              </UiTooltipContent>
            </UiTooltip>
          </UiTooltipProvider>
        ) : (
          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-primary/10 text-primary">
            {icon}
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {(description || trend) && (
          <p className="text-xs text-muted-foreground mt-1 flex items-center">
            {trend && (
              <>
                <span
                  className={
                    trend.isPositive
                      ? "text-green-600 mr-1"
                      : "text-red-600 mr-1"
                  }
                >
                  {trend.isPositive ? "↑" : "↓"}{" "}
                  {trend.absoluteChange !== undefined
                    ? `${Math.abs(trend.absoluteChange)} (${Math.abs(
                        trend.value
                      )}%)`
                    : `${Math.abs(trend.value)}%`}
                  <span className="ml-1">
                    vs {trend.period || "prev. period"}
                  </span>
                </span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="ml-1 cursor-help">
                        <Info className="h-3 w-3 text-muted-foreground" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                      <p>
                        This represents the change compared to{" "}
                        {trend.period || "the previous period"}.
                      </p>
                      {trend.absoluteChange !== undefined && (
                        <p className="mt-1">
                          Absolute change:{" "}
                          <span className="font-mono text-xs">
                            {trend.absoluteChange}
                          </span>
                        </p>
                      )}
                      <p className="mt-1">
                        Percentage calculated as:{" "}
                        <span className="font-mono text-xs">
                          (current - previous) / previous × 100%
                        </span>
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </>
            )}
            {description}
          </p>
        )}
        {footer && <div className="mt-2">{footer}</div>}
      </CardContent>
    </Card>
  );
};

export default StatsCard;
